import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bool, func, object, string, number } from 'prop-types';
import _ from 'lodash';
import { metric } from '@fiverr-private/perseus';
import { getUgcData } from '@fiverr-private/gig_listings/src/GigCardListings/externalUtils/getUgcData';
import { buildSourceInfo } from '../../../utils/ugc/buildSourceInfo';
import { getCurrentContent } from '../../../utils/ugc/getCurrentContent';
import { getOriginalContent } from '../../../utils/ugc/getOriginalContent';
import { isTranslated } from '../../../utils/ugc/isTranslated';
import { updateListingsUGC, fetchUGC } from '../../../actions/ugc';
import { getGigs } from '../../../utils/gigCardCarouselService';
import { FIT_MODAL_CLASS } from '../../../utils/constants';
import { PAGE_NAME } from '../../../utils/events';

const GigCardCarouselHOCWrapper = (WrappedComponent) => {

    const GigCardCarouselHOC = (props, context) => {
        const {
            shouldFetchCarousel,
            type,
            url,
            onEmptyGigs,
            updateCollections,
            collections,
            carouselPosition,
            carouselContext,
            source,
            isTouch,
            showTranslatedUGC,
            originalListingsUGC,
            currentListingsUGC,
            updateListingsUGC,
            fetchUGC
        } = props;

        const {
            locale,
            general,
            requestContext,
            currencyConverter
        } = context;

        const { current_user: currentUser } = requestContext;
        const { gigId, subCategorySlug } = general;

        const [gigs, setGigs] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [gigsTranslated, setGigsTranslated] = useState(false);

        useEffect(() => {
            if (!shouldFetchCarousel) {
                return;
            }

            if (isLoading && !gigs.length) {
                getGigsWrapper();
            } else if (!originalListingsUGC) {
                updateListingsUGC(getUgcData(gigs, locale), type);
            } else if (shouldFetchUGC()) {
                const sourceInfo = buildSourceInfo(({ gigId, user: currentUser }));
                fetchUGC({ sourceInfo, shouldToggle: false });
            } else if (shouldEnrichGigsWithListingUGC()) {
                enrichGigsWithListingUGC();
            }
        });

        const getGigsWrapper = async() => {
            try {
                const gigs = await getGigs({ url, type });
                reportFetchGigsData(false);
                setGigs(gigs);
                setIsLoading(false);
            } catch (ex) {
                setIsLoading(false);
                onEmptyGigs();
                reportFetchGigsData(true);
            }
        };

        const reportFetchGigsData = (emptyGigs) => {
            metric.count(`fetch_gigs_data.carousel.${type}.empty_gigs.${emptyGigs}`);
        };

        const shouldFetchUGC = () => showTranslatedUGC && !currentListingsUGC;

        const shouldEnrichGigsWithListingUGC = () => showTranslatedUGC && currentListingsUGC;

        const enrichGigsWithListingUGC = () => {
            /*
            * return if gigsTranslated is true is  needed here or else the component
            * will get into an infinite loop since we're changing the state during useEffect
            * */

            if (gigsTranslated) {
                return;
            }

            const enrichedGigs = gigs.map((gig) => ({
                ...gig,
                ...currentListingsUGC[gig.gig_id] || {}
            }));

            setGigsTranslated(true);
            setGigs(enrichedGigs);
        };

        const getContextEnrichment = () => {
            const { page_ctx_id, params } = requestContext;
            const { ref } = params;

            return ({
                gigCollect: {
                    gigCollectProps: {
                        showCollMenu: true,
                        updateCollections,
                        modalCustomName: FIT_MODAL_CLASS
                    },
                    collections: collections.collections
                },
                impressionEnrichment: {
                    listings: {
                        carousel_position: carouselPosition,
                        page_ctx_id
                    }
                },
                context: {
                    queryParameters: {
                        context: carouselContext,
                        ref
                    },
                    sourcePage: PAGE_NAME,
                    sourceComponent: source,
                    currency: currencyConverter.getCurrency(),
                    pageCtxId: page_ctx_id,
                    userId: currentUser ? currentUser.id : undefined,
                    subCategory: subCategorySlug,
                    viewportType: 'gigs.show',
                    currentPage: 'Gig page',
                    controllerAction: 'gigs#show'
                },
                isTouch
            });
        };

        if (!gigs.length && !isLoading) {
            return null;
        }

        return (
            <WrappedComponent {...props}
                gigs={gigs}
                isLoading={isLoading}
                contextEnrichment={getContextEnrichment()}/>
        );
    };

    GigCardCarouselHOC.propTypes = {
        showTranslatedUGC: bool,
        updateListingsUGC: func,
        type: string.isRequired,
        url: string.isRequired,
        onEmptyGigs: func,
        carouselPosition: number,
        collections: object,
        isTouch: bool,
        updateCollections: func,
        source: string,
        carouselContext: string,
        shouldFetchCarousel: bool
    };

    GigCardCarouselHOC.defaultProps = {
        showTranslatedUGC: false,
        updateListingsUGC: _.noop,
        onEmptyGigs: _.noop,
        collections: {},
        isTouch: false,
        updateCollections: _.noop,
        shouldFetchCarousel: true
    };

    GigCardCarouselHOC.contextTypes = {
        general: object,
        requestContext: object,
        currencyConverter: object,
        locale: string
    };

    const mapStateToProps = ({ ugc }, { type }) => {
        const { listings: originalListingsUGC } = getOriginalContent({ ugc });
        const { listings: currentListingsUGC } = getCurrentContent({ ugc });
        const showTranslatedUGC = isTranslated(ugc);

        return {
            originalListingsUGC: originalListingsUGC[type],
            currentListingsUGC: currentListingsUGC[type],
            showTranslatedUGC
        };
    };

    return connect(mapStateToProps, {
        updateListingsUGC,
        fetchUGC
    })(GigCardCarouselHOC);
};

export default GigCardCarouselHOCWrapper;
